import { onBeforeUnmount, onMounted, ref } from 'vue';
import getEmbeddedInModule from '@/helpers/getEmbeddedInModule';

// Boundary: the host page (friss-casemanagement claimsview.js) owns the scroll
// container and sizes the FSC Lite iframe to its full content height, so the
// iframe never scrolls internally. These postMessage types are the contract
// that lets embedded widgets learn where the visible viewport band currently
// sits inside the iframe document.
type FeedbackPositionSyncMessage = {
  type: 'claim-search-feedback-position-sync';
  payload?: {
    iframeTop?: number;
    viewportHeight?: number;
    offsetLeft?: number;
    offsetBottom?: number;
    offsetRight?: number;
  };
};

const DEFAULT_OFFSET = 16;

const isFeedbackPositionSyncMessage = (
  data: unknown
): data is FeedbackPositionSyncMessage => {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as FeedbackPositionSyncMessage).type ===
      'claim-search-feedback-position-sync'
  );
};

const toNumberOrDefault = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Why: module-level, so every consumer shares one view of the host band. PopoverRoot mounts at
// app start and keeps these current, so a widget that mounts minutes later (a toast) is placed
// correctly on its first paint instead of depending on a fresh ready -> position-sync round trip
// — which the host can legitimately never answer, leaving the widget positioned from nothing.
const iframeTop = ref(0);
const viewportHeight = ref(0);
const offsetLeft = ref(DEFAULT_OFFSET);
const offsetBottom = ref(DEFAULT_OFFSET);
const offsetRight = ref(DEFAULT_OFFSET);

let consumerCount = 0;

const handleHostMessage = (event: MessageEvent) => {
  // Security: only accept position-sync messages from the direct parent frame.
  // The host origin is not in runtime config so we cannot allowlist by origin
  // string, but verifying the source prevents arbitrary scripts (e.g. other
  // iframes in the same page) from spoofing the widget's position.
  if (
    event.source !== window.parent ||
    !isFeedbackPositionSyncMessage(event.data)
  ) {
    return;
  }

  const payload = event.data.payload;
  // Edge case: keep the previous value when the host omits a field so a
  // partial sync never zeroes out a known good position.
  iframeTop.value = toNumberOrDefault(payload?.iframeTop, iframeTop.value);
  viewportHeight.value = toNumberOrDefault(
    payload?.viewportHeight,
    viewportHeight.value
  );
  offsetLeft.value = toNumberOrDefault(payload?.offsetLeft, offsetLeft.value);
  offsetBottom.value = toNumberOrDefault(
    payload?.offsetBottom,
    offsetBottom.value
  );
  offsetRight.value = toNumberOrDefault(
    payload?.offsetRight,
    offsetRight.value
  );
};

// Why: one listener for all consumers, so the shared values keep updating for as long as any
// embedded widget is alive — not only while the widget that happened to subscribe first is.
const subscribeToHost = () => {
  consumerCount += 1;
  if (consumerCount > 1) {
    return;
  }

  window.addEventListener('message', handleHostMessage);
};

const unsubscribeFromHost = () => {
  consumerCount -= 1;
  if (consumerCount > 0) {
    return;
  }

  window.removeEventListener('message', handleHostMessage);
  // Why: with no consumer left nothing keeps the band current, so hold no stale values —
  // the next consumer to mount posts `ready` and is answered with a fresh sync.
  iframeTop.value = 0;
  viewportHeight.value = 0;
  offsetLeft.value = DEFAULT_OFFSET;
  offsetBottom.value = DEFAULT_OFFSET;
  offsetRight.value = DEFAULT_OFFSET;
};

/**
 * Asks the host to scroll its container by `top` pixels (negative scrolls up).
 *
 * Security: '*' target origin is acceptable: the payload is a scroll offset only. The host origin is
 * not in runtime config, so it cannot be allowlisted here.
 */
export const requestHostScroll = (top: number): void => {
  window.parent.postMessage(
    { type: 'claim-search-feedback-scroll-into-view', top },
    '*'
  );
};

/**
 * Shared host position-sync protocol for embedded (iframe) widgets.
 *
 * Exposes the raw values broadcast by the host:
 *  - `iframeTop`: the iframe's top relative to the host scroll container.
 *    Negative once the user scrolls the host down past the iframe top.
 *  - `viewportHeight`: the host's visible viewport height.
 *  - `offsetLeft` / `offsetBottom` / `offsetRight`: host-requested offsets for pinned widgets.
 *
 * The values are shared by every consumer and stay live for as long as one widget is mounted,
 * so a widget that mounts later reads the current band immediately. Widgets layer their own
 * positioning math on top of them.
 */
export const useEmbeddedHostSync = () => {
  const embeddedInModule = getEmbeddedInModule();
  const isEmbedded = embeddedInModule !== '' || window.self !== window.top;

  const ensureEmbeddedOverlayVisible = async () => {
    // Why: only ask the host to scroll when the iframe top is above the
    // visible area; otherwise the band-positioned overlay is already in view.
    if (!isEmbedded || iframeTop.value >= 0) {
      return;
    }

    requestHostScroll(iframeTop.value);

    // Why: wait one frame so the host can apply the scroll before the overlay opens.
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  };

  onMounted(() => {
    if (!isEmbedded) {
      return;
    }

    subscribeToHost();
    // Why: the host sends an initial position sync as soon as we signal ready.
    // Note: each component that calls useEmbeddedHostSync() posts this signal
    // independently on mount (e.g. ClaimSearchFeedback + PopoverRoot are both
    // mounted at the same time). The host must treat duplicate signals as
    // idempotent — it should respond with a single position sync, not one per
    // signal. A dropped answer is no longer fatal: the values above survive
    // from whichever consumer was synced before.
    window.parent.postMessage({ type: 'claim-search-feedback-ready' }, '*');
  });

  onBeforeUnmount(() => {
    if (!isEmbedded) {
      return;
    }

    unsubscribeFromHost();
  });

  return {
    isEmbedded,
    iframeTop,
    viewportHeight,
    offsetLeft,
    offsetBottom,
    offsetRight,
    ensureEmbeddedOverlayVisible
  };
};
